import { makeHighlight, getColor, buildColors, makeShades } from '@monobits/colors';
import { darken, lighten, tint, shade, timingFunctions } from 'polished';
import { buildFonts, fontlist } from '@monobits/fonts';
import { makeFluids, makeClamps } from '@monobits/scales';
import { reset, base, hacks } from '@monobits/styles';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var _PROD_ = !!(process.env.NODE_ENV === 'production');

var config = {
  initialColorModeName: 'light',
  useLocalStorage: true,
  useColorSchemeMediaQuery: _PROD_,
  colorTransitions: {
    duration: 0.35,
    ease: 'power1.inOut'
  }
};

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;

var modes = function modes(colors) {
  var _ = function _(ref) {
    return getColor(colors, ref);
  };

  return {
    light: {
      __icon: '🌞',
      text: _(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["dark"]))),
      background: _(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["light"]))),
      primary: _(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["yellow"]))),
      highlight: makeHighlight(_(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["yellow"]))))
    },
    dark: {
      __icon: '🌚',
      text: _(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["light"]))),
      background: _(_templateObject6 || (_templateObject6 = _taggedTemplateLiteralLoose(["dark"]))),
      primary: _(_templateObject7 || (_templateObject7 = _taggedTemplateLiteralLoose(["blue"]))),
      highlight: makeHighlight(_(_templateObject8 || (_templateObject8 = _taggedTemplateLiteralLoose(["blue"]))))
    }
  };
};

var colors = buildColors({
  modes: modes,
  colors: makeShades({
    light: [darken, '#ffffff'],
    dark: [lighten, '#000000'],
    yellow: [tint, '#FFBD2B'],
    blue: [shade, '#0759F9']
  }, [0.95, 0.75, 0.45, 0.15])
}, config);
var fluid$1 = makeFluids({
  'size-text': ['18px', '30px'],
  'size-caption': ['24px', '44px'],
  'size-title': ['45px', '180px']
}, ['375px', '2500px']);
var scales$1 = {
  fontSizes: fluid$1.scale,
  fontWeights: {
    body: 400,
    heading: 400
  },
  lineHeights: {
    body: 1.35,
    heading: 1
  }
};

var rootFontStyles = _extends({
  fontSize: 1,
  fontFamily: 'helvetica',
  lineHeight: 'body',
  fontWeight: 'body',
  '.-rte + .-rte': {
    mt: '2em'
  }
}, fluid$1.properties);

var fonts = buildFonts([fontlist(['helvetica']), scales$1], {
  fonts: {
    heading: 'inherit',
    body: 'inherit',
    monospace: 'Menlo, monospace'
  },
  variants: {
    key: 'text',
    heading: {
      variant: 'text.title'
    },
    __default: {
      variant: 'text.text'
    },
    __cropped: {
      title: {
        fontSize: 3,
        fontFamily: 'heading',
        fontWeight: 'body',
        lineHeight: 'heading',
        crops: 'helvetica'
      },
      caption: {
        fontSize: 2,
        fontFamily: 'heading',
        fontWeight: 'heading',
        lineHeight: 'body',
        crops: 'helvetica'
      },
      text: {
        fontSize: 1,
        fontFamily: 'body',
        fontWeight: 'body',
        lineHeight: 'body',
        crops: 'helvetica'
      }
    }
  }
});
var fluid = makeFluids({
  'padding-x': ['15px', '60px'],
  'padding-s': ['24px', '68px'],
  'padding-m': ['30px', '75px'],
  'padding-l': ['45px', '90px']
}, ['375px', '2500px']);
var stroke = makeClamps([['1.5px', '0.15vw', '2.45px'], ['2px', '0.2vw', '3.5px'], ['2.5px', '0.3vw', '4px']], '--stroke');

var rootScaleStyles = _extends({}, fluid.properties, stroke.properties);

var scales = {
  space: fluid.scale,
  borderWidths: stroke.scale,
  sizes: {
    sidebar: 256,
    colorswitch: '1.15em',
    container: 1000
  },
  radii: {
    default: 3,
    medium: 6,
    large: 10
  },
  zIndices: {
    below: -1,
    default: 1,
    nav: 2,
    popup: 3,
    modal: 5,
    panel: 4
  }
};
var burger = {
  __default: {
    '--gap': ['3.5', null, '3'],
    '--stroke': 'var(--stroke-x, 0.07em)',
    '--speed': '0.45s',
    '--ease': timingFunctions('easeOutCubic'),
    '--color': function color(_ref) {
      var colors = _ref.colors;
      return colors.text;
    },
    width: '2rem'
  }
};
var button = {
  styles: {
    '--speed': '0.35s',
    '--ease': timingFunctions('easeOutCubic'),
    '--disabled-opacity': 0.25
  },
  variants: {
    primary: {
      '--text': function text(t) {
        return t.colors.text;
      },
      '--text-hover': function textHover(t) {
        return t.colors.text;
      },
      '--bg': function bg(t) {
        return t.colors.primary;
      },
      '--bg-hover': function bgHover(t) {
        return t.colors.primary[3];
      }
    },
    inverted: {
      '--text': function text(t) {
        return t.colors.background;
      },
      '--text-hover': function textHover(t) {
        return t.colors.background;
      },
      '--bg': function bg(t) {
        return t.colors.text;
      },
      '--bg-hover': function bgHover(t) {
        return t.colors.subdued;
      }
    },
    ghost: {
      '--text': function text(t) {
        return t.colors.text;
      },
      '--text-hover': function textHover(t) {
        return t.colors.text;
      },
      '--bg': 'transparent',
      '--bg-hover': function bgHover(t) {
        return t.colors.muted;
      }
    },
    __default: {
      variant: 'button.variants.primary'
    }
  },
  sizes: {
    md: {
      p: '0.5em 0.75em'
    },
    __default: {
      variant: 'button.sizes.md'
    }
  }
};
var link = {
  rte: {
    textDecoration: 'underline',
    textUnderlineOffset: '0.2em'
  },
  label: {
    color: 'muted',
    fontSize: 2
  },
  underlined: {
    fontSize: 1,
    '--stroke': 'var(--stroke-x, 0.07em)',
    '--gap': '-0.15em',
    '--speed': '0.35s',
    '--ease': timingFunctions('easeOutCubic')
  }
};
var padding = 1;

var height = function height(property) {
  return "calc((var(--height, 100) * " + property + " - var(--offset, 0px))";
};

var layout = {
  container: {
    full: {
      px: padding,
      maxWidth: 'none'
    },
    box: {
      p: padding,
      maxWidth: 'none'
    },
    wrapper: {
      px: padding,
      maxWidth: 'container'
    },
    boxwrap: {
      p: padding,
      maxWidth: 'container'
    },
    __default: {
      variant: 'layout.container.full'
    }
  },
  section: {
    small: {
      my: '4%'
    },
    regular: {
      my: '6%'
    },
    large: {
      my: '8%'
    },
    strict: {
      my: 0,
      '--calc': height('var(--vh, 1vh))'),
      height: 'var(--calc)'
    },
    hero: {
      my: 0,
      '--calc': height('var(--vh, 1vh))'),
      minHeight: 'var(--calc)'
    },
    vh: {
      my: 0,
      '--calc': height('var(--vh-x, 1vh))'),
      minHeight: 'var(--calc)'
    },
    __default: {
      variant: 'layout.section.regular'
    }
  }
};
var icon = {
  size: {
    '--stroke-absolute': 'calc(var(--stroke-x, 1.5px) * 0.75)',
    '--stroke-relative': '3px',
    size: '100%'
  },
  variants: {
    scale: {
      variant: 'icon.size',
      '*': {
        strokeWidth: 'var(--stroke-relative, 4px)'
      }
    },
    noscale: {
      variant: 'icon.size',
      '*': {
        strokeWidth: 'var(--stroke-absolute, 2px)',
        vectorEffect: 'non-scaling-stroke'
      }
    },
    __default: {
      variant: 'icon.variants.noscale'
    }
  }
};
var colorswitch = {
  styles: {
    width: 'fit-content',
    gridAutoFlow: 'column',
    gap: '0.5em'
  },
  noText: {
    '.name': {
      display: 'none'
    }
  },
  variants: {
    bullet: {
      minWidth: 'var(--size)',
      minHeight: 'var(--size)',
      '--size': 'clamp(14px, 0.75rem, 18px)',
      display: 'block',
      borderRadius: '50%',
      overflow: 'hidden',
      variant: 'colorswitch.noText'
    },
    badge: {
      py: '0.5em',
      px: '0.75em',
      fontSize: 1,
      display: 'block',
      borderRadius: '300px'
    },
    __default: {
      variant: 'colorswitch.variants.bullet'
    }
  }
};
var variants = /*#__PURE__*/Object.freeze({
  __proto__: null,
  burger: burger,
  button: button,
  link: link,
  layout: layout,
  icon: icon,
  colorswitch: colorswitch
});

var preset = _extends({
  config: config,
  breakpoints: ['43em', '62em', '82em'],
  styles: {
    root: _extends({}, reset, base, rootFontStyles, rootScaleStyles, hacks(['killThemeTransitions', 'aspectRatio', 'userSelect']))
  }
}, scales, colors, fonts, variants, {
  motion: {
    defaults: {
      duration: 1,
      ease: 'easeInOutCubic'
    }
  }
});

export { scales$1 as defaultScales, preset };